import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabButton from "../../../Components/common/TabButton/TabButton";
import ReactTableWrapper from "../../../Components/common/ReactTableWrapper/ReactTableWrapper";
import { getStoredPageSize } from "../../../Components/common/PageSizeSelect/PageSizeSelect";
import YaraMatchesDetail from "./components/YaraMatchesDetail";

import {
  yaraResultsByHostHeader,
  yaraResultsByRuleHeader,
} from "../../../utils/constants/tableHeaders";

import {
  RequestYaraResultsList,
  SetYaraResultsActiveTab,
} from "../../../store/actions/integrationsActions";

import "./styles.scss";

const PAGE_SIZE = 10;
const TABLE_NAME = "yaraResults";
const GROUP_BY_RULE = "Group by Rule";
const GROUP_BY_HOST = "Group by Host";

const YaraResults = () => {
  const dispatch = useDispatch();

  const list = useSelector((state) => state.integrations.yaraResultsList);
  const listLoading = useSelector((state) => state.integrations.yaraResultsListLoading);
  const pageCount = useSelector((state) => state.integrations.yaraResultsListPageCount);
  const activeTab = useSelector((state) => state.integrations.yaraResultsActiveTab);
  const isTabSwitching = useSelector((state) => state.integrations.yaraResultsTabSwitching);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize(TABLE_NAME, PAGE_SIZE));
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
  const [tableFilters, setTableFilters] = useState([]);

  useEffect(() => {
    if (selectedTimeFilter) {
      dispatch(
        RequestYaraResultsList({
          pageSize,
          pageNumber,
          selectedTimeFilter,
          filters: tableFilters,
        }),
      );
    }

    if (tableFilters.length === 0) {
      const interval = setInterval(() => {
        if (selectedTimeFilter) {
          dispatch(
            RequestYaraResultsList({
              pageNumber: pageNumber < 1 ? 1 : pageNumber,
              pageSize,
              selectedTimeFilter,
              filters: [],
            }),
          );
        }
      }, 20000);

      return () => clearInterval(interval);
    }
  }, [dispatch, pageNumber, pageSize, selectedTimeFilter, activeTab, tableFilters]);

  useEffect(
    () => () => localStorage.setItem(`${TABLE_NAME}Toggle`, JSON.stringify({ ids: [] })),
    [],
  );

  const filterChangeHandler = (newFilters) => setTableFilters(newFilters);

  const refreshHandler = () =>
    dispatch(
      RequestYaraResultsList({
        pageSize,
        pageNumber,
        selectedTimeFilter,
        filters: tableFilters,
      }),
    );

  // The two tabs have different columns, so the table is remounted (see `key`
  // below) instead of carrying the other tab's column filter and expanded rows.
  const tabChangeHandler = (tab) => {
    if (tab === activeTab) return;

    setPageNumber(1);
    setTableFilters([]);
    localStorage.setItem(`${TABLE_NAME}Toggle`, JSON.stringify({ ids: [] }));
    dispatch(SetYaraResultsActiveTab(tab));
  };

  return (
    <div className="yara-results">
      <div className="yara-results__header">
        <h1>Yara Results</h1>
        <p>Below list is displaying custom yara rule matches collected from agents:</p>
      </div>
      <div className="yara-results__table border">
        <div className="button-block">
          <TabButton
            title={GROUP_BY_RULE}
            onClick={() => tabChangeHandler(GROUP_BY_RULE)}
            active={activeTab}
          />
          <TabButton
            title={GROUP_BY_HOST}
            onClick={() => tabChangeHandler(GROUP_BY_HOST)}
            active={activeTab}
          />
        </div>
        <ReactTableWrapper
          key={activeTab}
          data={list}
          isDataLoading={listLoading}
          tableName={TABLE_NAME}
          columns={activeTab === GROUP_BY_HOST ? yaraResultsByHostHeader : yaraResultsByRuleHeader}
          paginationButtons
          customPageSize={PAGE_SIZE}
          refreshConfig={{ show: true, onClickHandler: refreshHandler }}
          styles={{ table: isTabSwitching ? { opacity: "0.4" } : {} }}
          expandConfig={{
            use: true,
            expandOnRowClick: true,
            subComponent: YaraMatchesDetail,
            syncToggle: true,
          }}
          pagination={{
            manualPagination: true,
            totalPageCount: pageCount,
            serverSidePaginationNumber: pageNumber,
            setServerSidePaginationNumber: setPageNumber,
            onPageSizeChange: setPageSize,
          }}
          filterByTime={useMemo(
            () => ({
              show: true,
              label: "Filter by last: ",
              selectedTimeFilter: selectedTimeFilter,
              setSelectedTimeFilter: setSelectedTimeFilter,
            }),
            [selectedTimeFilter, setSelectedTimeFilter],
          )}
          onFilterChange={filterChangeHandler}
        />
      </div>
    </div>
  );
};

export default YaraResults;
